import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { useOfflineDetection } from './useOfflineDetection';
import { useOfflineStorage, SyncQueueItem, CachedScript } from './useOfflineStorage';
import { useToast } from './use-toast';
import { useTranslation } from 'react-i18next';
import { SyncConflict } from '@/components/pwa/ConflictResolutionDialog';
import { showSyncNotification } from '@/lib/pushNotifications';
import { getNotificationPreferences } from '@/components/pwa/notificationPreferences';

export const useSyncQueue = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { isOnline } = useOfflineDetection();
  const { getSyncQueue, clearSyncQueue, removeCachedScript, getCachedScript, cacheScript } = useOfflineStorage();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [currentConflict, setCurrentConflict] = useState<SyncConflict | null>(null);
  const [conflictQueue, setConflictQueue] = useState<SyncQueueItem[]>([]);
  const [resolvedItems, setResolvedItems] = useState<Set<string>>(new Set());

  const checkPendingCount = useCallback(async () => {
    const queue = await getSyncQueue();
    setPendingCount(queue.length);
  }, [getSyncQueue]);

  const checkForConflict = useCallback(async (item: SyncQueueItem): Promise<SyncConflict | null> => {
    if (item.action !== 'update' || !item.data) return null;

    try {
      // Get the server version
      const { data: serverScript, error } = await supabase
        .from('scripts')
        .select('*')
        .eq('id', item.scriptId)
        .single();

      if (error || !serverScript) return null;

      // Get the cached version
      const cachedScript = await getCachedScript(item.scriptId);
      if (!cachedScript) return null;

      // Check if server was updated after our cache
      const serverUpdatedAt = new Date(serverScript.updated_at || 0);
      const cachedAt = new Date(cachedScript.cachedAt);
      const localUpdatedAt = new Date(item.timestamp);

      // Conflict exists if server was updated after we cached but before our local edit
      // or if content differs and both have been modified
      if (serverUpdatedAt > cachedAt && serverScript.content !== item.data.content) {
        return {
          scriptId: item.scriptId,
          scriptTitle: item.data.title || serverScript.title || 'Untitled',
          localVersion: {
            content: item.data.content || '',
            updatedAt: localUpdatedAt,
            wordCount: item.data.word_count || 0
          },
          serverVersion: {
            content: serverScript.content || '',
            updatedAt: serverUpdatedAt,
            wordCount: serverScript.word_count || 0
          }
        };
      }

      return null;
    } catch (err) {
      console.error('Error checking for conflict:', err);
      return null;
    }
  }, [getCachedScript]);

  const finishSync = useCallback(async () => {
    await clearSyncQueue();
    setPendingCount(0);
    setIsSyncing(false);
    setResolvedItems(new Set());
    
    // Show notification if enabled
    const prefs = getNotificationPreferences();
    if (prefs.syncUpdates) {
      showSyncNotification('complete');
    }
    
    toast({
      title: t('offline.syncComplete'),
      description: t('offline.syncCompleteDesc')
    });
  }, [clearSyncQueue, toast, t]);

  const resolveConflict = useCallback(async (choice: 'local' | 'server') => {
    if (!currentConflict) return;

    const conflictItem = conflictQueue.find(item => item.scriptId === currentConflict.scriptId);
    
    if (choice === 'local' && conflictItem?.data) {
      // Apply local changes to server
      try {
        const { error } = await supabase
          .from('scripts')
          .update({
            title: conflictItem.data.title,
            content: conflictItem.data.content,
            settings: conflictItem.data.settings as Json,
            word_count: conflictItem.data.word_count,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentConflict.scriptId);

        if (error) throw error;

        toast({
          title: t('offline.conflict.resolved'),
          description: t('offline.conflict.localApplied')
        });
      } catch (err) {
        console.error('Error applying local version:', err);
        toast({
          title: t('offline.conflict.error'),
          description: t('offline.conflict.errorApplying'),
          variant: 'destructive'
        });
      }
    } else if (choice === 'server') {
      // Update local cache with server version
      try {
        const { data: serverScript } = await supabase
          .from('scripts')
          .select('*')
          .eq('id', currentConflict.scriptId)
          .single();

        if (serverScript) {
          await cacheScript({
            id: serverScript.id,
            title: serverScript.title || 'Untitled',
            content: serverScript.content || '',
            settings: (serverScript.settings || {}) as object,
            scene_type: serverScript.scene_type || 'recording',
            word_count: serverScript.word_count || 0
          });
        }

        toast({
          title: t('offline.conflict.resolved'),
          description: t('offline.conflict.serverApplied')
        });
      } catch (err) {
        console.error('Error applying server version:', err);
      }
    }

    // Mark as resolved
    setResolvedItems(prev => new Set([...prev, currentConflict.scriptId]));
    setCurrentConflict(null);

    // Check for more conflicts
    const remainingConflicts = conflictQueue.filter(
      item => item.scriptId !== currentConflict.scriptId
    );
    setConflictQueue(remainingConflicts);

    if (remainingConflicts.length === 0) {
      // All conflicts resolved, continue syncing
      await finishSync();
    }
  }, [currentConflict, conflictQueue, cacheScript, toast, t, finishSync]);

  const skipConflict = useCallback(() => {
    if (!currentConflict) return;

    // Mark as skipped (will try again next time)
    setCurrentConflict(null);

    const remainingConflicts = conflictQueue.filter(
      item => item.scriptId !== currentConflict.scriptId
    );
    setConflictQueue(remainingConflicts);

    if (remainingConflicts.length === 0) {
      setIsSyncing(false);
    }
  }, [currentConflict, conflictQueue]);

  const processSyncQueue = useCallback(async (): Promise<boolean> => {
    if (isSyncing) return false;

    setIsSyncing(true);
    try {
      const queue = await getSyncQueue();
      if (queue.length === 0) {
        setIsSyncing(false);
        return true;
      }

      // Sort by timestamp to process in order
      const sortedQueue = queue.sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      const conflicts: SyncQueueItem[] = [];
      const nonConflictItems: SyncQueueItem[] = [];

      // Check for conflicts first
      for (const item of sortedQueue) {
        if (resolvedItems.has(item.scriptId)) {
          continue; // Already resolved this session
        }

        const conflict = await checkForConflict(item);
        if (conflict) {
          conflicts.push(item);
          setConflictQueue(prev => [...prev, item]);
          if (!currentConflict) {
            setCurrentConflict(conflict);
            // Send conflict notification
            const prefs = getNotificationPreferences();
            if (prefs.syncUpdates) {
              showSyncNotification('conflict', `"${conflict.scriptTitle}" has conflicting changes`);
            }
          }
        } else {
          nonConflictItems.push(item);
        }
      }

      // Process non-conflict items
      let hasErrors = false;
      for (const item of nonConflictItems) {
        try {
          if (item.action === 'update' && item.data) {
            const { error } = await supabase
              .from('scripts')
              .update({
                title: item.data.title,
                content: item.data.content,
                settings: item.data.settings as Json,
                word_count: item.data.word_count,
                updated_at: new Date().toISOString()
              })
              .eq('id', item.scriptId);

            if (error) {
              console.error('Sync error for script:', item.scriptId, error);
              hasErrors = true;
            }
          } else if (item.action === 'delete') {
            const { error } = await supabase
              .from('scripts')
              .delete()
              .eq('id', item.scriptId);

            if (error) {
              console.error('Sync delete error for script:', item.scriptId, error);
              hasErrors = true;
            } else {
              await removeCachedScript(item.scriptId);
            }
          }
        } catch (err) {
          console.error('Sync processing error:', err);
          hasErrors = true;
        }
      }

      // If there are conflicts, wait for resolution
      if (conflicts.length > 0) {
        return false; // Will continue after conflict resolution
      }

      if (!hasErrors) {
        await finishSync();
      } else {
        setIsSyncing(false);
      }

      return !hasErrors;
    } catch (err) {
      console.error('Sync queue error:', err);
      setIsSyncing(false);
      return false;
    }
  }, [isSyncing, getSyncQueue, checkForConflict, removeCachedScript, finishSync, currentConflict, resolvedItems]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline) {
      checkPendingCount().then(() => {
        if (pendingCount > 0) {
          processSyncQueue();
        }
      });
    }
  }, [isOnline, checkPendingCount, pendingCount, processSyncQueue]);

  // Check pending count on mount
  useEffect(() => {
    checkPendingCount();
  }, [checkPendingCount]);

  return {
    isSyncing,
    pendingCount,
    processSyncQueue,
    checkPendingCount,
    currentConflict,
    resolveConflict,
    skipConflict
  };
};
